import type React from "react";
import { twJoin } from "tailwind-merge";

import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { UsersCheckIcon } from "@/components/Icons/UsersCheckIcon";
import type { PillListItem } from "@/components/Pill/PillList";
import { Badge } from "@/design-system/Badge";
import { buttonVariants } from "@/design-system/Button";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

type AudienceSelectorButtonProps = {
  items: PillListItem[];
  onClick: () => void;
  isError?: boolean;
  isDisabled?: boolean;
  "data-testid"?: string;
};

export function AudienceSelectorButton({
  items,
  isDisabled,
  isError,
  onClick,
  "data-testid": dataTestId,
  ...otherProps
}: AudienceSelectorButtonProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Tooltip tooltip={isDisabled ? t("page.admin-create-post.audience-selector.tooltip") : undefined}>
      <button
        type="button"
        data-testid={dataTestId}
        disabled={isDisabled}
        onClick={onClick}
        className={twResolve(
          buttonVariants({
            variant: "ghostSecondary",
            size: "sm",
            fullWidth: true,
            content: "body",
            pressAnimation: false,
          }),
          "group/audience-selector-btn h-auto! shrink items-center justify-between gap-space-sm overflow-visible rounded-lg! p-space-sm! text-left whitespace-normal ring-1 ring-border-secondary transition-opacity hover:ring-border-tertiary focus-visible:ring-border-default disabled:cursor-not-allowed disabled:opacity-40",
          isError && "border-border-danger-secondary",
        )}
        {...otherProps}
      >
        <div className="flex min-w-0 flex-1 flex-wrap justify-between gap-space-sm">
          <div className="flex shrink-0 items-center gap-space-sm">
            <UsersCheckIcon size={20} />
            <span>{t("page.admin-create-post.audience-selector.label")}:</span>
          </div>

          <ul className="flex max-w-full flex-wrap items-center gap-space-sm">
            {items.map((item) => (
              <li
                key={item.label}
                className={twJoin(
                  "max-w-full",
                  isError && "[&>span]:bg-background-danger-secondary [&>span]:text-text-danger-secondary",
                )}
              >
                <Badge variant="white" icon={item.icon} prefix={item.prefix}>
                  {item.label}
                </Badge>
              </li>
            ))}
          </ul>
        </div>

        {/* pr-space-xs brings the chevron to the same 0.75rem inset as the design-system Select */}
        <span className="flex shrink-0 pr-space-xs text-text-secondary group-hover/audience-selector-btn:text-text-primary">
          <ChevronRightIcon />
        </span>
      </button>
    </Tooltip>
  );
}
